import { forwardRef, useCallback, useLayoutEffect, useRef, type TextareaHTMLAttributes } from "react";
import s from "./Textarea.module.css";

type TextareaElementProps = TextareaHTMLAttributes<HTMLTextAreaElement>;

export interface TextareaProps extends TextareaElementProps {
  invalid?: boolean;
  /** Height in px the textarea grows to before it caps and scrolls
      internally. */
  maxHeight?: number;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { invalid, className, "aria-invalid": ariaInvalid, maxHeight = 200, style, onInput, value, ...rest },
  ref,
) {
  const innerRef = useRef<HTMLTextAreaElement | null>(null);

  const setRefs = useCallback(
    (node: HTMLTextAreaElement | null) => {
      innerRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref],
  );

  const grow = useCallback(
    (el: HTMLTextAreaElement) => {
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
    },
    [maxHeight],
  );

  const handleInput = useCallback<NonNullable<TextareaElementProps["onInput"]>>(
    (event) => {
      grow(event.currentTarget);
      onInput?.(event);
    },
    [grow, onInput],
  );

  // Re-measure on every value change, not just on user input: a
  // programmatic reset (e.g. clearing the field after save) needs the same
  // shrink that typing would trigger.
  useLayoutEffect(() => {
    if (innerRef.current) {
      grow(innerRef.current);
    }
  }, [grow, value]);

  const classes = className ? `${s.root} ${className}` : s.root;
  return (
    <textarea
      ref={setRefs}
      className={classes}
      aria-invalid={ariaInvalid ?? invalid}
      style={{ ...style, maxHeight }}
      onInput={handleInput}
      value={value}
      rows={1}
      {...rest}
    />
  );
});
